import React, { useState, useMemo } from 'react';
import {
  pickNonemptyDetections,
  mapConditionToDbConcern,
  CATEGORY_COLORS as categoryColors,
  VIEW_ORDER,
} from '../../utils/hairReportUtils';
import RadarChart from '../../components/RadarChart';

export default function HairDiagnosticSection({
  hairResults,
  dbRecommendations,
  recsLoading,
  onAddToCart,
}) {
  const [activeTab, setActiveTab] = useState('concerns');
  const [selectedCondition, setSelectedCondition] = useState(null);

  const globalDetections = useMemo(() => {
    return pickNonemptyDetections(
      hairResults?.results?.detections,
      hairResults?.detections
    );
  }, [hairResults]);

  const backendConcerns = useMemo(() => {
    if (!hairResults) return [];

    const positionResults = hairResults.position_results || hairResults.positionResults || {};
    const fromViews = [];
    VIEW_ORDER.forEach((view) => {
      const pos = positionResults[view];
      pickNonemptyDetections(
        pos?.raw_detections,
        pos?.detections,
        pos?.results?.detections
      ).forEach((d) => fromViews.push(d));
    });

    const detections = pickNonemptyDetections(fromViews, globalDetections);

    const groups = {};

    detections.forEach(d => {
      const name = d.label || d.pure_label || 'Unknown';
      const nameLower = name.toLowerCase();
      if (nameLower === 'healthy scalp' || nameLower === 'normal scalp') return;

      const conf = parseFloat(d.confidence) || 0;
      if (!groups[name]) {
        groups[name] = {
          name: name,
          total: 0,
          color: categoryColors[name] || d.color || '#db2777'
        };
      }
      groups[name].total = Math.max(groups[name].total, conf);
    });

    return Object.values(groups).map((g, i) => {
      const val = Math.min(100, g.total);
      return {
        key: `cond-${i}`,
        label: g.name,
        value: val,
        val: `${val.toFixed(1)}%`,
        status: val < 3.0 ? 'LOW' : val >= 10.0 ? 'SEVERE' : val >= 5.0 ? 'MODERATE' : 'FAIR',
        color: g.color
      };
    }).sort((a, b) => b.value - a.value);
  }, [hairResults, globalDetections]);

  const displayConditionLabel = selectedCondition || backendConcerns[0]?.label || null;

  const activeProducts = useMemo(() => {
    const dbConcern = mapConditionToDbConcern(displayConditionLabel);
    return dbConcern ? (dbRecommendations[dbConcern] || []) : [];
  }, [displayConditionLabel, dbRecommendations]);

  React.useEffect(() => {
    if (!backendConcerns.length) {
      setSelectedCondition(null);
      return;
    }
    setSelectedCondition((prev) => {
      if (prev && backendConcerns.some((c) => c.label === prev)) return prev;
      return backendConcerns[0].label;
    });
  }, [backendConcerns]);

  const confidenceData = useMemo(() => {
    if (!hairResults) return { level: 'High', value: 98, reason: 'Waiting for AI processing.' };

    const baseConf = hairResults.confidence || 72;
    if (backendConcerns.length === 0) return { level: 'High', value: Math.min(99, baseConf), reason: 'AI scan completed with high precision. No significant concerns detected.' };

    const avgConf = backendConcerns.reduce((acc, curr) => acc + curr.value, 0) / backendConcerns.length;
    const finalVal = Math.min(99, Math.max(45, baseConf + (avgConf > 50 ? 5 : -5)));

    let level = 'High';
    if (finalVal < 60) level = 'Low';
    else if (finalVal < 85) level = 'Medium';

    const reason = finalVal > 88
      ? 'High-fidelity scan: AI has successfully mapped all visible hair and scalp patterns.'
      : `AI Scan: Analyzing ${backendConcerns.length} localized concerns with high-sensitivity surface mapping.`;

    return { level, value: finalVal, reason };
  }, [backendConcerns, hairResults]);

  const reportData = useMemo(() => {
    const insights = [];
    const attention = [];
    const overview = [
      { label: 'Shine', status: 'Normal', icon: '✨' },
      { label: 'Volume', status: 'Medium', icon: '🧬' },
      { label: 'Thickness', status: 'Medium', icon: '📏' },
      { label: 'Texture', status: hairResults?.hair_type || 'Wavy', icon: '〰️' },
    ];

    const labels = globalDetections.map(c => c.label.toLowerCase());

    if (labels.some(l => l.includes('thinning') || l.includes('baldness'))) {
      attention.push({ label: 'Hair Density', status: 'Moderate', icon: '👤' });
      insights.push("Lower density detected at crown area compared to sides.");
      overview[1].status = 'Low';
    } else {
      attention.push({ label: 'Hair Density', status: 'Healthy', icon: '👤' });
    }

    if (labels.some(l => l.includes('dandruff') || l.includes('scaling'))) {
      attention.push({ label: 'Scalp Condition', status: 'Mild Dandruff', icon: '🔬' });
      insights.push("Mild dandruff detected on scalp. Recommend clarifying treatment.");
    } else {
      attention.push({ label: 'Scalp Condition', status: 'Clean', icon: '🔬' });
    }

    if (labels.some(l => l.includes('recession') || l.includes('hairline'))) {
      attention.push({ label: 'Hairline Status', status: 'Slight Recession', icon: '💇' });
      insights.push("Slight recession observed at hairline.");
    }

    if (labels.some(l => l.includes('rough') || l.includes('straw'))) {
      attention.push({ label: 'Frizz Level', status: 'Medium', icon: '🌩️' });
      insights.push("Frizz is more visible near hairline and sides.");
      overview[0].status = 'Dry';
    }

    if (labels.some(l => l.includes('bug') || l.includes('insect'))) {
      attention.push({ label: 'Scalp Parasites', status: 'Active Bugs', icon: '🕷️' });
      insights.push("Hair bugs detected. Immediate specialized anti-parasitic treatment required.");
    }

    if (labels.some(l => l.includes('rash') || l.includes('pimple'))) {
      attention.push({ label: 'Inflammation', status: 'Irritated', icon: '🔴' });
      insights.push("Scalp inflammation or pimples observed. Recommend soothing treatment.");
    }

    if (insights.length === 0) {
      insights.push("Scalp appears healthy and well-hydrated.");
      insights.push("Hair cuticle shows good alignment and shine.");
    }

    return { attention, overview, insights };
  }, [globalDetections, hairResults]);

  const etiologyReasons = useMemo(() => {
    const reasons = [];
    const attentionLabels = reportData.attention.map(a => a.label.toLowerCase());

    if (attentionLabels.some(l => l.includes('dandruff') || l.includes('scalp'))) {
      reasons.push("Potential overactive sebaceous glands or microbial (Malassezia) imbalance on the scalp surface.");
    }
    if (attentionLabels.some(l => l.includes('density') || l.includes('hairline'))) {
      reasons.push("Follicle miniaturization patterns often linked to hormonal shifts, prolonged stress, or localized nutrient deficiencies.");
    }
    if (attentionLabels.some(l => l.includes('frizz') || l.includes('texture'))) {
      reasons.push("Elevated hair porosity and cuticle lifting due to environmental humidity or lack of protective lipid layers.");
    }
    if (attentionLabels.some(l => l.includes('shine') || l.includes('dry'))) {
      reasons.push("Oxidative stress or frequent heat exposure stripping the natural moisture-retaining proteins from the hair shaft.");
    }
    if (attentionLabels.some(l => l.includes('oil') || l.includes('greasy'))) {
      reasons.push("Irregular washing frequency or harsh product build-up disrupting the scalp's natural pH and moisture barrier.");
    }

    if (reasons.length === 0) {
      reasons.push("High-fidelity synthesis confirms a balanced hair cycle and optimal nutrient delivery to the follicles.");
    }

    return reasons;
  }, [reportData.attention]);

  const radarData = useMemo(() => {
    const labels = globalDetections.map(c => c.label.toLowerCase());
    const densityVal = labels.some(l => l.includes('thinning') || l.includes('baldness')) ? 62 : 94;
    const shineVal = labels.some(l => l.includes('rough') || l.includes('straw')) ? 58 : 92;
    const frizzVal = labels.some(l => l.includes('frizz') || l.includes('texture')) ? 64 : 88;
    const volumeVal = labels.some(l => l.includes('thinning')) ? 60 : 90;
    const oilVal = labels.some(l => l.includes('oil') || l.includes('greasy')) ? 68 : 95;

    return {
      "Density": densityVal,
      "Shine": shineVal,
      "Frizz": frizzVal,
      "Volume": volumeVal,
      "Oil Balance": oilVal,
    };
  }, [globalDetections]);

  if (!hairResults) {
    return (
      <section id="diagnostic" className="hair-report-diagnostic">
        <div className="results-main-layout">
          <div className="content-container">
            <div className="error-container">
              <h2>Analysis Data Unavailable</h2>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const overallScore = hairResults.hair_score || hairResults.hairScore || 48;

  return (
    <section id="diagnostic" className="hair-report-diagnostic">
      <div className="hair-results-page skin-parity">
      <main className="results-main-layout">
        <div className="content-container">
          <div className="diagnostic-left-column">
            <div className="confidence-level-box-premium">
              <div className="confidence-header">
                <div className="check-icon-circle">✓</div>
                <div className="confidence-info">
                  <span className="conf-label">CONFIDENCE LEVEL</span>
                  <span className="conf-value">{confidenceData.level} ({confidenceData.value}%)</span>
                </div>
              </div>
              <p className="conf-reason">Reason: {confidenceData.reason}</p>
            </div>

            <div className="advanced-report-container left-panel-report">
            <div className="report-badge">AI CLINICAL SYNTHESIS</div>
            <div className="report-grid">
              <div className="report-section attention-section">
                <h3 className="section-title">NEEDS ATTENTION</h3>
                <div className="attention-list">
                  {reportData.attention.map((item, idx) => (
                    <div key={idx} className="attention-item">
                      <div className="item-main">
                        <span className="item-icon">{item.icon}</span>
                        <span className="item-label">{item.label}</span>
                      </div>
                      <span className={`item-status ${item.status.toLowerCase().includes('healthy') ? 'status-good' : 'status-alert'}`}>
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="ai-summary-premium-wrap">
              <div className="summary-main-card">
                <div className="summary-header">
                  <div className="summary-title-row">
                    <span className="summary-badge">CERTIFIED AI DIAGNOSTIC</span>
                  </div>
                </div>

                <div className="summary-three-column-grid">
                  <div className="summary-col">
                    <div className="col-header">
                      <span className="col-icon">📄</span>
                      <h5>Summary</h5>
                    </div>
                    <p className="col-text">
                      {overallScore > 88
                        ? "Your hair shows high resilience with stable density. Scalp health is optimal with minor maintenance required for environmental protection."
                        : "Synthesis identifies active concerns in density or scalp condition. Corrective care is recommended to stabilize follicle health and reduce inflammation."}
                    </p>
                  </div>

                  <div className="summary-col">
                    <div className="col-header">
                      <span className="col-icon alert-icon">⚠️</span>
                      <h5>Key Concerns</h5>
                    </div>
                    <p className="col-text highlights-text">
                      {reportData.attention.map(a => a.label).join(", ") || "No major concerns detected."}
                    </p>
                  </div>

                  <div className="summary-col">
                    <div className="col-header">
                      <span className="col-icon lightbulb-icon">💡</span>
                      <h5>Why This Is Happening</h5>
                    </div>
                    <div className="col-text reasons-list-wrap">
                      {etiologyReasons.map((reason, idx) => (
                        <div key={idx} className="reason-bullet-item">
                          <span className="reason-dot">•</span>
                          {reason}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>

          <div className="dashboard-panel">
            <div className="dashboard-header-bar">
              <div className="score-display-group">
                <span className="score-label">Hair Score:</span>
                <div className="score-value-box-wrapper">
                  <span className="score-value-box">{overallScore}</span>
                  <div className="score-meter-mini">
                    <div className="meter-fill" style={{ width: `${overallScore}%` }}></div>
                  </div>
                </div>
              </div>
              <div className="environment-stats">
                <div className="env-pill">🌡️ 34.3°C</div>
                <div className="env-pill">AQI: <span className="stat-moderate">Moderate</span></div>
                <div className="env-pill">UV: <span className="stat-extreme">Extreme</span></div>
              </div>
            </div>

            <div className="dashboard-tabs-container">
              <div className="tabs-header-parity">
                <button
                  className={`tab-trigger-parity ${activeTab === 'concerns' ? 'active' : ''}`}
                  onClick={() => setActiveTab('concerns')}
                >
                  Concerns
                </button>
                <button
                  className={`tab-trigger-parity ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
              </div>

              <div className="tab-content-area-parity">
                {activeTab === 'concerns' ? (
                  <div className="concerns-tab-view">
                    <p className="tab-instruction-text">Tap any concern to view recommended products.</p>

                    {backendConcerns.length > 0 ? (
                      <>
                        <div className="prob-box-header-wrap">
                          <h4 className="prob-title">DETAILED CONDITION PROBABILITY</h4>
                        </div>

                        <div className="concerns-tab-content-premium">
                          <div className="prob-rows-container">
                            {backendConcerns.map(cond => (
                              <div
                                key={cond.key}
                                className={`prob-row clickable ${selectedCondition === cond.label ? 'selected' : ''}`}
                                onClick={() => setSelectedCondition(selectedCondition === cond.label ? null : cond.label)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setSelectedCondition(selectedCondition === cond.label ? null : cond.label);
                                  }
                                }}
                              >
                                <div className="prob-header">
                                  <span className="prob-label" style={{ color: cond.color, fontWeight: '700', fontSize: '0.95rem' }}>
                                    {cond.label}
                                  </span>
                                  <div className="prob-meta">
                                    <span className={`prob-status`}>{cond.status}</span>
                                    <span className="prob-percent" style={{ backgroundColor: cond.color }}>{cond.val}</span>
                                  </div>
                                </div>
                                <div className="prob-progress-track">
                                  <div className="prob-progress-fill" style={{ width: `${cond.value}%`, backgroundColor: cond.color }}></div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {displayConditionLabel && (recsLoading || activeProducts.length > 0) && (
                            <div className="selected-condition-recommendations animate-slide-up" style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                              <h4 style={{ fontSize: '0.9rem', fontWeight: '900', color: '#db2777', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Recommended Products for {displayConditionLabel}
                              </h4>
                              <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '1rem' }}>
                                Full active-ingredient options for this specific concern. Your routine section below shows a separate daily essentials lineup.
                              </p>
                              {recsLoading ? (
                                <p style={{ fontSize: '0.8rem', color: '#64748b', margin: 0 }}>Loading personalized recommendations...</p>
                              ) : (
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '1rem' }}>
                                {activeProducts.map((p, idx) => (
                                  <div key={idx} style={{ background: '#fff', borderRadius: '18px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ width: '100%', height: '110px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <img src={p.image_url} alt={p.product_name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '0.8rem' }} />
                                    </div>
                                    <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                      <h5 style={{ fontSize: '0.85rem', fontWeight: '800', color: '#1e293b', margin: '0 0 0.4rem 0', lineHeight: '1.3' }}>{p.product_name}</h5>
                                      <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '0 0 0.8rem 0', lineHeight: '1.4', flexGrow: 1, minHeight: '4rem', overflow: 'hidden' }}>{p.description}</p>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                        <span style={{ fontSize: '0.9rem', fontWeight: '900', color: '#db2777' }}>₹{750 + (idx * 250)}</span>
                                        <button
                                          type="button"
                                          className="add-to-cart-action-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onAddToCart?.(p, {
                                              sourceSection: 'diagnostic_recommendations',
                                              priceInr: 750 + idx * 250,
                                              hairConcern: displayConditionLabel,
                                            });
                                          }}
                                        >
                                          ADD TO CART
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              )}
                            </div>
                          )}

                        </div>
                      </>
                    ) : (
                      <div className="no-concerns-detected-box">
                        <div className="success-check-icon">✓</div>
                        <h3>Excellent Hair Health</h3>
                        <p>No significant hair or scalp concerns were detected in this analysis.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="overview-tab-view">
                    <p className="tab-instruction-text">Tap a chart point to view that concern.</p>
                    <div className="overview-chart-section">
                      <RadarChart data={radarData} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="dashboard-summary-rebalanced">
              <div className="report-section overview-section-rebalanced">
                <h3 className="section-title">CONDITION OVERVIEW</h3>
                <div className="overview-grid">
                  {reportData.overview.map((item, idx) => (
                    <div key={idx} className="overview-item">
                      <div className="item-header">
                        <span className="item-icon">{item.icon}</span>
                        <span className="item-label">{item.label}</span>
                      </div>
                      <span className="item-status-pill">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="report-section insights-section-rebalanced">
                <h3 className="section-title">PROFESSIONAL INSIGHTS</h3>
                <ul className="insights-list">
                  {reportData.insights.map((text, idx) => (
                    <li key={idx}>{text}</li>
                  ))}
                </ul>
              </div>
            </div>

            <p className="medical-disclaimer-text">
              This is not a medical diagnosis. Consult a trichologist for health concerns.
            </p>
          </div>
        </div>
      </main>
      </div>
    </section>
  );
}
